import $ from "jquery";
import Service from '../service';
import Request from '../util/request';

export default class Auth {
    private static activeInstance: Auth | null = null;

    public verified: string | null = null;

    public timestamp: number = 0;
    public status: any = null;
    public loading: any = null;
    public session: any = {};

    private unauthorizedCheckPromise: Promise<boolean> | null = null;
    private redirecting: boolean = false;

    constructor(public service: Service) {
        this.request = new Request();
        Auth.activeInstance = this;
        this.bindApiInterceptor();
    }

    public async init() {
        this.loading = null;

        try {
            const response: any = await this.request.post('/auth/check');
            if (!response || response.code !== 200) {
                this.clearLocalSession();
            } else {
                this.applySessionResponse(response.data || {});
            }
        } catch (e) {
            this.clearLocalSession();
        }

        this.timestamp = new Date().getTime();
        this.loading = true;
        return this;
    }

    private applySessionResponse(data: any) {
        const session: any = data.session || {};
        this.verified = session.verified || null;
        this.session = session;
        this.status = data.status === true && Boolean(session.id);
    }

    private clearLocalSession() {
        this.verified = null;
        this.session = {};
        this.status = false;
    }

    public async update() {
        while (this.loading === null) {
            await this.service.render(100);
        }

        const diff = new Date().getTime() - this.timestamp;
        if (diff > 1000 * 60) {
            await this.init();
        }
    }

    public isLoggedIn(): boolean {
        return this.status === true && Boolean(this.session && this.session.id);
    }

    public isPublicPath(pathname: string = location.pathname): boolean {
        let normalized = pathname || '/';
        if (normalized.length > 1)
            normalized = normalized.replace(/\/+$/, '');
        return normalized === '/access' || normalized === '/register';
    }

    public safeReturnTo(value: any, fallback: string = '/dashboard'): string {
        if (
            typeof value !== 'string' ||
            !value.startsWith('/') ||
            value.startsWith('//') ||
            value.indexOf('\\') >= 0
        )
            return fallback;

        try {
            const target = new URL(value, location.origin);
            if (
                target.origin !== location.origin ||
                this.isPublicPath(target.pathname) ||
                target.pathname.startsWith('/auth/')
            )
                return fallback;
            return target.pathname + target.search + target.hash;
        } catch (e) {
            return fallback;
        }
    }

    public requireLogin(): boolean {
        if (this.isLoggedIn())
            return true;

        this.redirectToLogin('login-required');
        return false;
    }

    public redirectToLogin(reason: string = 'expired') {
        if (this.redirecting || this.isPublicPath())
            return;

        const returnTo = this.safeReturnTo(
            location.pathname + location.search + location.hash,
            '/dashboard'
        );
        const query = new URLSearchParams();
        query.set('returnTo', returnTo);
        if (reason)
            query.set('reason', reason);

        this.redirecting = true;
        location.replace('/access?' + query.toString());
    }

    private responseData(value: any): any {
        if (value && typeof value === 'object' && value.code !== undefined)
            return value;

        if (value && value.responseJSON)
            return value.responseJSON;

        if (value && value.responseText) {
            try {
                return JSON.parse(value.responseText);
            } catch (e) { }
        }

        return null;
    }

    private shouldInspectUnauthorized(response: any, requestUrl: string): boolean {
        return Boolean(
            response &&
            Number(response.code) === 401 &&
            requestUrl.indexOf('/wiz/api/') >= 0 &&
            requestUrl.indexOf('/page.access/login') < 0 &&
            requestUrl.indexOf('/page.register/register') < 0 &&
            !this.isPublicPath()
        );
    }

    private async confirmUnauthorized(): Promise<boolean> {
        try {
            const response: any = await this.request.post('/auth/check');
            if (!response || response.code !== 200)
                return false;

            const data: any = response.data || {};
            this.timestamp = new Date().getTime();
            this.loading = true;

            if (data.status === true && data.session && data.session.id) {
                this.applySessionResponse(data);
                return false;
            }

            this.clearLocalSession();
            this.redirectToLogin('expired');
            return true;
        } catch (e) {
            return false;
        }
    }

    private async handleUnauthorizedResponse(response: any, requestUrl: string): Promise<boolean> {
        if (!this.shouldInspectUnauthorized(response, requestUrl))
            return false;

        if (this.redirecting)
            return true;

        if (!this.unauthorizedCheckPromise)
            this.unauthorizedCheckPromise = this.confirmUnauthorized();

        const pending = this.unauthorizedCheckPromise;
        try {
            return await pending;
        } finally {
            if (this.unauthorizedCheckPromise === pending)
                this.unauthorizedCheckPromise = null;
        }
    }

    private bindApiInterceptor() {
        const jquery: any = $ as any;
        const originalAjax: any = jquery.ajax;

        if (!originalAjax || originalAjax.__seasonAuthInterceptor)
            return;

        const wrappedAjax: any = function (...args: any[]) {
            const jqXHR: any = originalAjax.apply(this, args);
            const settings: any = typeof args[0] === 'string' ? (args[1] || {}) : (args[0] || {});
            const requestUrl: string = typeof args[0] === 'string'
                ? args[0]
                : String(settings.url || '');

            if (!jqXHR || typeof jqXHR.always !== 'function' || requestUrl.indexOf('/wiz/api/') < 0)
                return jqXHR;

            const originalAlways: any = jqXHR.always.bind(jqXHR);
            jqXHR.always = function (...callbacks: any[]) {
                const wrappedCallbacks = callbacks.map((callback: any) => {
                    if (typeof callback !== 'function')
                        return callback;

                    return function (...callbackArgs: any[]) {
                        const auth = Auth.activeInstance;
                        if (!auth)
                            return callback.apply(this, callbackArgs);

                        const response = auth.responseData(callbackArgs[0]);
                        if (!auth.shouldInspectUnauthorized(response, requestUrl))
                            return callback.apply(this, callbackArgs);

                        auth.handleUnauthorizedResponse(response, requestUrl)
                            .then((redirected: boolean) => {
                                if (!redirected)
                                    callback.apply(this, callbackArgs);
                            })
                            .catch(() => callback.apply(this, callbackArgs));
                    };
                });

                originalAlways(...wrappedCallbacks);
                return jqXHR;
            };

            return jqXHR;
        };

        wrappedAjax.__seasonAuthInterceptor = true;
        jquery.ajax = wrappedAjax;
    }

    public check: any = new Proxy(((root) => {
        let obj: any = () => {
            return this.isLoggedIn();
        }

        obj.root = root;
        return obj;
    })(this), {
        get(target, propKey) {
            let propValue: any = target.root.session[propKey];

            return (values: any = null) => {
                if (values === null) {
                    return true;
                }

                if (typeof values == 'boolean') {
                    if (values === propValue)
                        return true;
                    return false;
                }

                if (typeof values == 'string')
                    values = [values];

                if (values.indexOf(propValue) >= 0) {
                    return true;
                }

                return false;
            };
        }
    });

    public allow: any = new Proxy(((root) => {
        let obj: any = (redirect: any = null) => {
            if (root.check()) return true;
            if (redirect) location.href = redirect;
            return false;
        }

        obj.root = root;
        obj.check = root.check;
        return obj;
    })(this), {
        get(target, propKey) {
            return (values: any = null, redirect: any = null) => {
                if (target.check[propKey](values)) return true;
                if (redirect) location.href = redirect;
                return false;
            }
        }
    });

    public hash(password: string = '') {
        return this.service.crypto.SHA256(password).toString();
    }
}
